import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import dAppLogo from '../../assets/dashboard-application.png';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useSidebarData } from '../sidebar/hooks/useSidebarData';
import { getDappHomePath } from '../../utils/dappRoutes';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';

export const DashboardMain = () => {
  usePageTitle('Dashboard | Publishing Portal');
  const navigate = useNavigate();
  const { setVisible } = useWalletModal();
  const [authState] = useAuth();
  const { activePublisher, isLoading: isPublisherLoading } = usePublisher();
  const {
    data: {
      publisherUserJoinId: sidebarPublisherUserJoinId,
      kybStatusData,
      hasKybStatusData,
      dapps,
    },
    isLoading: isSidebarDataLoading,
  } = useSidebarData();
  const wallet = useWallet();
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const isWalletConnected = wallet.connected;
  const isSidebarDataCurrent =
    sidebarPublisherUserJoinId ===
    (activePublisher?.publisherUserJoinId ?? null);
  const isBootstrapLoading =
    isPublisherLoading ||
    (Boolean(activePublisher) &&
      (!isSidebarDataCurrent || isSidebarDataLoading));
  const publisherKybStatus = isSidebarDataCurrent
    ? (kybStatusData?.status ?? activePublisher?.kybStatus)
    : activePublisher?.kybStatus;
  const publisherVerification = getPublisherVerificationGate({
    status: publisherKybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const statusMessage = (() => {
    if (activePublisher && !publisherVerification.isApproved) {
      return (
        <div className="rounded-lg border border-neutral-800/60 bg-neutral-900/80 px-4 py-3 text-center text-sm text-neutral-300">
          {publisherVerification.actionMessage}
        </div>
      );
    }

    if (!isWalletConnected) {
      return (
        <button
          type="button"
          onClick={() => setVisible(true)}
          className="w-60 h-9 px-4 py-5 bg-[#2a2a2a] rounded-xl inline-flex justify-center items-center gap-2 transition-all duration-300 ease-out hover:bg-[#3a3a3a] group relative"
        >
          <span className="text-white text-sm font-light leading-none">+</span>
          <span className="text-white text-sm font-medium font-['Inter'] leading-tight">
            Connect Wallet
          </span>
        </button>
      );
    }

    return null;
  })();

  useEffect(() => {
    if (authState.type !== AuthStateType.Authenticated) {
      setIsCheckingStatus(false);
      return;
    }

    if (isBootstrapLoading) {
      setIsCheckingStatus(true);
      return;
    }

    if (!activePublisher) {
      navigate('/publisher-access', { replace: true });
      return;
    }

    setIsCheckingStatus(true);

    if (!hasKybStatusData || !kybStatusData) {
      setIsCheckingStatus(false);
      return;
    }

    const status = kybStatusData.status;
    const verificationApproved = getPublisherVerificationGate({
      status,
      publisherType: activePublisher.publisherType,
      kycKybBypassEnabled: activePublisher.kycKybBypassEnabled,
    }).isApproved;

    if (!verificationApproved) {
      setIsCheckingStatus(false);
      return;
    }

    if (dapps.length > 0) {
      navigate(getDappHomePath(dapps[0]), { replace: true });
      return;
    }

    setIsCheckingStatus(false);
  }, [
    activePublisher,
    authState.type,
    dapps,
    hasKybStatusData,
    isBootstrapLoading,
    kybStatusData,
    navigate,
  ]);

  if (isCheckingStatus) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full text-white overflow-hidden">
      <div className="flex flex-col justify-start items-center gap-12 max-w-80">
        <div className="flex flex-col justify-start items-center gap-4">
          <div className="size-32 relative overflow-hidden group transition-all duration-500 ease-in-out hover:scale-105">
            <img
              src={dAppLogo}
              alt="dApp Logo"
              className="w-full h-full object-contain transition-all duration-500 ease-in-out"
            />
          </div>

          <div className="text-center flex flex-col gap-1">
            <div className="text-xl font-semibold leading-7">
              You haven't added a dApp!
            </div>
            <div className="text-neutral-400 text-sm leading-tight">
              Let's add your first dApp to the Solana dApp Store!
            </div>
          </div>
        </div>

        {statusMessage && (
          <div className="mt-6 w-full max-w-64">{statusMessage}</div>
        )}
      </div>
    </div>
  );
};
